/**
 * Angles — logique pure (Boîte à outils, 6e → 4e).
 *
 * Programme 6e (BO 2025) : lexique (nul, aigu, droit, obtus, plat),
 * rapporteur, bissectrice, somme des angles d'un triangle et triangles
 * particuliers ; 5e (BO du 5 mars 2026) : angles alternes-internes et
 * correspondants, parallélisme et réciproques, démonstration de la somme
 * des angles par les alternes-internes. Mesures en degrés ENTIERS quand on
 * les règle ; pour un triangle sur quadrillage, arrondis au degré dont la
 * somme reste 180.
 */

export interface Pt {
  x: number;
  y: number;
}

export function angleKind(deg: number): string {
  if (deg === 0) return 'nul';
  if (deg < 90) return 'aigu';
  if (deg === 90) return 'droit';
  if (deg < 180) return 'obtus';
  return 'plat';
}

export function kindSay(deg: number): string {
  const k = angleKind(deg);
  if (k === 'nul') return 'Angle nul : les deux côtés sont confondus.';
  if (k === 'aigu') return `Angle aigu : ${deg}° est entre 0° et 90°.`;
  if (k === 'droit') return 'Angle droit : 90°, comme le coin d’une équerre.';
  if (k === 'obtus') return `Angle obtus : ${deg}° est entre 90° et 180°.`;
  return 'Angle plat : 180°, les deux côtés sont dans le prolongement l’un de l’autre.';
}

/* ------------------------------------------------------------------ */
/* rapporteur                                                          */
/* ------------------------------------------------------------------ */

/**
 * Le rapporteur a deux graduations : l'extérieure part de 0 à DROITE, la
 * graduation intérieure part de 0 à GAUCHE. Le premier côté [OA) est à
 * droite ou à gauche ; le second côté fait la direction `phi` (0 à 180°,
 * comptée depuis la droite, dans le sens inverse des aiguilles d'une montre).
 */
export function protractor(
  firstLeft: boolean,
  phi: number,
): {
  measure: number;
  outer: number;
  inner: number;
  /** la graduation à lire : celle qui a son 0 sur [OA) */
  good: 'outer' | 'inner';
} {
  const outer = phi;
  const inner = 180 - phi;
  return firstLeft
    ? { measure: inner, outer, inner, good: 'inner' }
    : { measure: outer, outer, inner, good: 'outer' };
}

/** réponse lue au rapporteur : juste, l'autre graduation, ou à côté */
export function readingFeedback(firstLeft: boolean, phi: number, answer: number): string {
  const r = protractor(firstLeft, phi);
  if (answer === r.measure) return `Oui : l’angle mesure ${r.measure}°.`;
  const other = r.good === 'outer' ? r.inner : r.outer;
  if (answer === other && other !== r.measure)
    return `Tu as lu l’autre graduation. Pars du 0 qui est sur le côté [OA), ${firstLeft ? 'à gauche' : 'à droite'} : c’est la graduation ${r.good === 'outer' ? 'extérieure' : 'intérieure'}.`;
  if (Math.abs(answer - r.measure) <= 2)
    return 'Presque : regarde bien la graduation la plus proche du côté.';
  return 'Non. Repère le 0 qui est sur le côté [OA), puis suis cette graduation jusqu’au côté [OB).';
}

/* ------------------------------------------------------------------ */
/* triangle                                                            */
/* ------------------------------------------------------------------ */

const sub = (a: Pt, b: Pt) => ({ x: a.x - b.x, y: a.y - b.y });
const dot = (u: Pt, v: Pt) => u.x * v.x + u.y * v.y;
const cross = (u: Pt, v: Pt) => u.x * v.y - u.y * v.x;
const len2 = (u: Pt) => dot(u, u);

/** angle en degrés (réel) au sommet p, entre p→q et p→r */
export function angleAt(p: Pt, q: Pt, r: Pt): number {
  const u = sub(q, p);
  const v = sub(r, p);
  return (Math.atan2(Math.abs(cross(u, v)), dot(u, v)) * 180) / Math.PI;
}

/**
 * Arrondis au degré dont la somme fait exactement `total`. Les valeurs d'un
 * même groupe (angles égaux d'un triangle isocèle) reçoivent le même
 * arrondi ; on cherche, parmi les arrondis à ±1 près, celui qui s'écarte le
 * moins des valeurs exactes, sans afficher 0° pour un vrai angle.
 */
export function roundToTotal(
  values: readonly number[],
  total: number,
  groups?: ReadonlyArray<readonly number[]>,
): number[] {
  const gs: number[][] = groups
    ? groups.map((g) => [...g])
    : values.reduce<number[][]>((acc, v, i) => {
        const g = acc.find((x) => Math.abs(values[x[0]!]! - v) < 1e-9);
        if (g) g.push(i);
        else acc.push([i]);
        return acc;
      }, []);
  let best: number[] | null = null;
  let bestCost = Infinity;
  const deltas = [-1, 0, 1, 2];
  const choose = (k: number, cur: number[]) => {
    if (k === gs.length) {
      const sum = cur.reduce((s, v) => s + v, 0);
      if (sum !== total) return;
      if (cur.some((v, i) => v < 1 && values[i]! > 0)) return;
      const cost = cur.reduce((s, v, i) => s + Math.abs(v - values[i]!), 0);
      if (cost < bestCost - 1e-12) {
        bestCost = cost;
        best = [...cur];
      }
      return;
    }
    for (const d of deltas) {
      const next = [...cur];
      for (const i of gs[k]!) next[i] = Math.floor(values[i]! + 1e-9) + d;
      choose(k + 1, next);
    }
  };
  choose(
    0,
    values.map(() => 0),
  );
  return best ?? values.map(Math.round);
}

export interface TriangleInfo {
  /** angles en A, B, C (degrés réels) */
  exact: [number, number, number];
  /** arrondis au degré, somme 180 */
  shown: [number, number, number];
  /** exactement droit (produit scalaire nul : sommets sur le quadrillage) */
  rightAt: number | null;
  /** côtés égaux (carrés des longueurs exacts) */
  isosceles: Array<[number, number]>;
  degenerate: boolean;
  say: string;
}

const NAMES = ['A', 'B', 'C'] as const;

export function triangleInfo(a: Pt, b: Pt, c: Pt): TriangleInfo {
  const pts = [a, b, c];
  const degenerate = cross(sub(b, a), sub(c, a)) === 0;
  const exact = [angleAt(a, b, c), angleAt(b, c, a), angleAt(c, a, b)] as [number, number, number];
  // angles égaux : ceux qui font face à des côtés de même longueur (exact sur le quadrillage)
  const sidesFirst = [len2(sub(b, c)), len2(sub(c, a)), len2(sub(a, b))];
  const groups: number[][] = [];
  [0, 1, 2].forEach((i) => {
    const g = groups.find((x) => sidesFirst[x[0]!] === sidesFirst[i]);
    if (g) g.push(i);
    else groups.push([i]);
  });
  const shown = (degenerate ? exact.map(Math.round) : roundToTotal(exact, 180, groups)) as [
    number,
    number,
    number,
  ];
  let rightAt: number | null = null;
  pts.forEach((p, i) => {
    const q = pts[(i + 1) % 3]!;
    const r = pts[(i + 2) % 3]!;
    if (!degenerate && dot(sub(q, p), sub(r, p)) === 0) rightAt = i;
  });
  // côtés : [BC] opposé à A, [CA] opposé à B, [AB] opposé à C
  const sides = [len2(sub(b, c)), len2(sub(c, a)), len2(sub(a, b))];
  const isosceles: Array<[number, number]> = [];
  for (let i = 0; i < 3; i++)
    for (let j = i + 1; j < 3; j++) if (sides[i] === sides[j]) isosceles.push([i, j]);
  let say: string;
  if (degenerate)
    say = 'Les trois points sont alignés : ce n’est pas un vrai triangle (angles 0°, 0° et 180°).';
  else {
    const parts: string[] = [];
    if (rightAt !== null)
      parts.push(`rectangle en ${NAMES[rightAt]} : les deux autres angles font 90° à eux deux`);
    if (isosceles.length === 3) parts.push('équilatéral : trois angles de 60°');
    else if (isosceles.length === 1) {
      // sommet principal : celui qui n'est opposé à aucun des deux côtés égaux
      const [i, j] = isosceles[0]!;
      const apex = 3 - i - j;
      parts.push(
        `isocèle en ${NAMES[apex]} : les angles en ${NAMES[i]} et en ${NAMES[j]} sont égaux`,
      );
    }
    say = parts.length ? `Triangle ${parts.join(', et ')}.` : 'Triangle quelconque.';
  }
  return { exact, shown, rightAt, isosceles, degenerate, say };
}

/* ------------------------------------------------------------------ */
/* deux droites et une sécante                                         */
/* ------------------------------------------------------------------ */

export type Corner = 'ne' | 'nw' | 'sw' | 'se';
export type Pair = 'opp' | 'corr' | 'alt';

/**
 * d₁ horizontale (en haut), d₂ inclinée de `beta` degrés (en bas), sécante
 * de direction `alpha` (0 < alpha < 180, depuis la droite). Mesure de
 * l'angle dans chaque coin autour des deux points d'intersection.
 */
export function cornerAngle(line: 1 | 2, corner: Corner, alpha: number, beta: number): number {
  const a = line === 1 ? alpha : alpha - beta;
  return corner === 'ne' || corner === 'sw' ? a : 180 - a;
}

export const PAIRS: Record<Pair, { label: string; corners: [[1 | 2, Corner], [1 | 2, Corner]] }> = {
  opp: {
    label: 'Opposés par le sommet',
    corners: [
      [1, 'ne'],
      [1, 'sw'],
    ],
  },
  corr: {
    label: 'Correspondants',
    corners: [
      [1, 'ne'],
      [2, 'ne'],
    ],
  },
  alt: {
    label: 'Alternes-internes',
    corners: [
      [1, 'sw'],
      [2, 'ne'],
    ],
  },
};

export function pairSay(pair: Pair, alpha: number, beta: number): string {
  const [[l1, c1], [l2, c2]] = PAIRS[pair].corners;
  const x = cornerAngle(l1, c1, alpha, beta);
  const y = cornerAngle(l2, c2, alpha, beta);
  if (pair === 'opp')
    return `Deux angles opposés par le sommet ont la même mesure : ${x}° et ${y}°.`;
  const name = pair === 'corr' ? 'correspondants' : 'alternes-internes';
  if (beta === 0)
    return `Les droites sont parallèles : les angles ${name} sont égaux (${x}° et ${y}°).`;
  return `Les angles ${name} mesurent ${x}° et ${y}° : ils ne sont pas égaux, donc les droites ne sont pas parallèles.`;
}
